import React, { createContext, useContext, useMemo, useReducer, useEffect } from 'react';

import { toISODate } from '@libs/NwMoment';

import { resolveFilters, saveFilters, saveExpand, loadExpand } from './StatementFilters';

/**
 * StatementContext
 *
 * Universal context provider for contact statements.
 * Manages state for all contact types in a unified way.
 */

const emptyStatementContextValue = {
    statementType: 'model', // model, agency, client, service
    contactID: null,
    contactType: 'model', // model, agency, client, service
    reload: false,
    loading: false,
    alwaysEnabled: true,
    filters: null,
    expand: false,
    showSubjectNameInTitle: false,
    scope: 'statement', // statement, search
    persistFilters: true,
    filterOverrides: null,
};

const StatementContext = createContext(emptyStatementContextValue);
const StatementDispatchContext = createContext(null);

const getApiUrl = (statementType, contactID, range) => {
    let apiUrl = '';
    if (statementType === 'model') {
        apiUrl = `models/statements/${contactID}?`;
    } else {
        apiUrl = `customers/${statementType}/statements/${contactID}?`;
    }
    apiUrl += `dateFrom=${range[0]}&dateTo=${range[1]}`;
    return apiUrl;
};

const getPaymentsApiUrl = (contactID, range) => {
    return `payments/contact/${contactID}?dateFrom=${range[0]}&dateTo=${range[1]}`;
};

const getRange = (range) => {
    return [toISODate(range[0]), toISODate(range[1])];
};

const persistStatementFilters = (state, nextFilters) => {
    if (state.persistFilters) {
        saveFilters(state.contactType, state.statementType, nextFilters);
    }
};

const applyFilterOverrides = (baseFilters, state) => {
    if (!state.filterOverrides) {
        return baseFilters;
    }
    return {
        ...baseFilters,
        ...state.filterOverrides,
    };
};

const statementReducer = (state, action) => {
    const { statementType, contactID, contactType, filters, scope, persistFilters } = state;

    switch (action.type) {
        case 'ChangeStatementType': {
            const loadedFilters = resolveFilters(contactType, action.value);
            const newFilters = persistFilters
                ? loadedFilters
                : applyFilterOverrides(
                      {
                          ...loadedFilters,
                          range: filters.range,
                          period: filters.period,
                      },
                      state
                  );
            const storedExpand = scope === 'search' ? state.expand : loadExpand(contactType, action.value);
            return {
                ...state,
                statementType: action.value,
                filters: newFilters,
                expand: scope === 'search' ? state.expand : (storedExpand ?? false),
                apiUrl: getApiUrl(action.value, contactID, newFilters.range),
                paymentsApiUrl: getPaymentsApiUrl(contactID, newFilters.range),
            };
        }
        case 'ChangeContactType': {
            const loadedFilters = resolveFilters(action.value, statementType);
            const newFilters = persistFilters
                ? loadedFilters
                : applyFilterOverrides(
                      {
                          ...loadedFilters,
                          range: filters.range,
                          period: filters.period,
                      },
                      state
                  );
            const storedExpand = scope === 'search' ? state.expand : loadExpand(action.value, statementType);
            return {
                ...state,
                contactType: action.value,
                filters: newFilters,
                expand: scope === 'search' ? state.expand : (storedExpand ?? false),
                apiUrl: getApiUrl(statementType, contactID, newFilters.range),
                paymentsApiUrl: getPaymentsApiUrl(contactID, newFilters.range),
            };
        }
        case 'ChangeContactID':
            return {
                ...state,
                contactID: action.value,
                apiUrl: getApiUrl(statementType, action.value, filters.range),
                paymentsApiUrl: getPaymentsApiUrl(action.value, filters.range),
            };
        case 'UpdateRange': {
            const rangeToSave = getRange(action.value);
            const newFiltersWithRange = {
                ...filters,
                range: rangeToSave,
            };
            persistStatementFilters(state, newFiltersWithRange);
            return {
                ...state,
                filters: {
                    ...newFiltersWithRange,
                },
                apiUrl: getApiUrl(statementType, contactID, rangeToSave),
                paymentsApiUrl: getPaymentsApiUrl(contactID, rangeToSave),
            };
        }
        case 'UpdateFilters': {
            const newFilters = {
                ...filters,
                [action.name]: action.value,
            };
            persistStatementFilters(state, newFilters);
            return {
                ...state,
                filters: newFilters,
            };
        }
        case 'UpdateExpand':
            if (scope !== 'search' && persistFilters) {
                saveExpand(contactType, statementType, action.value);
            }
            return {
                ...state,
                expand: action.value,
            };
        case 'StartReload':
            return {
                ...state,
                reload: true,
            };
        case 'StopReload':
            return {
                ...state,
                reload: false,
            };
        case 'SetLoading':
            return {
                ...state,
                loading: action.value,
            };
        default:
            return state;
    }
};

export const StatementContextProvider = ({
    defaultStatementType,
    defaultContactID,
    defaultContactType,
    showSubjectNameInTitle = false,
    scope = 'statement',
    children,
    expand = false,
    initialRange,
    initialPeriod,
    persistFilters = true,
    filterOverrides,
}) => {
    const resolvedFilters = useMemo(() => {
        const filters = resolveFilters(defaultContactType, defaultStatementType);
        let nextFilters = filters;
        if (initialRange) {
            nextFilters = {
                ...nextFilters,
                range: getRange(initialRange),
                period: initialPeriod || nextFilters.period,
            };
        }
        if (filterOverrides) {
            nextFilters = {
                ...nextFilters,
                ...filterOverrides,
            };
        }
        return nextFilters;
    }, [defaultContactType, defaultStatementType, initialRange, initialPeriod, filterOverrides]);

    const storedExpand = useMemo(
        () => loadExpand(defaultContactType, defaultStatementType),
        [defaultContactType, defaultStatementType]
    );

    const contextData = useMemo(
        () => ({
            ...emptyStatementContextValue,
            statementType: defaultStatementType,
            contactID: defaultContactID,
            contactType: defaultContactType,
            reload: false,
            filters: resolvedFilters,
            apiUrl: getApiUrl(defaultStatementType, defaultContactID, resolvedFilters.range),
            paymentsApiUrl: getPaymentsApiUrl(defaultContactID, resolvedFilters.range),
            expand: scope === 'search' ? expand : (storedExpand ?? false),
            showSubjectNameInTitle,
            scope,
            persistFilters,
            filterOverrides: filterOverrides || null,
        }),
        [
            defaultStatementType,
            defaultContactID,
            defaultContactType,
            resolvedFilters,
            storedExpand,
            expand,
            showSubjectNameInTitle,
            scope,
            persistFilters,
            filterOverrides,
        ]
    );

    const [data, dispatch] = useReducer(statementReducer, contextData);

    useEffect(() => {
        if (scope === 'search') {
            dispatch({ type: 'UpdateExpand', value: expand });
        }
    }, [expand, scope]);

    useEffect(() => {
        if (defaultContactID) {
            dispatch({ type: 'ChangeContactID', value: defaultContactID });
        }
    }, [defaultContactID]);

    useEffect(() => {
        if (defaultContactType && defaultContactType !== data.contactType) {
            dispatch({ type: 'ChangeContactType', value: defaultContactType });
        }
    }, [defaultContactType]);

    useEffect(() => {
        if (defaultStatementType && defaultStatementType !== data.statementType) {
            dispatch({ type: 'ChangeStatementType', value: defaultStatementType });
        }
    }, [defaultStatementType]);

    return (
        <StatementContext.Provider value={data}>
            <StatementDispatchContext.Provider value={dispatch}>{children}</StatementDispatchContext.Provider>
        </StatementContext.Provider>
    );
};

export const useStatementContext = () => {
    const context = useContext(StatementContext);
    if (!context) {
        throw new Error('useStatementContext must be used within StatementContextProvider');
    }
    return context;
};

export const useStatementDispatch = () => {
    const context = useContext(StatementDispatchContext);
    if (!context) {
        throw new Error('useStatementDispatch must be used within StatementContextProvider');
    }
    return context;
};
